import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import fetchCategoryWiseProduct from '../helpers/fetchCategoryWiseProduct';
import displayINRCurrency from '../helpers/displayCurrency';
import addToCart from '../helpers/addToCart';
import scrollTop from '../helpers/scrollTop';
import Context from '../context';

const CategoryWiseProductDisplay = ({ category, heading }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadingList = new Array(13).fill(null);

  const { fetchUserAddToCart } = useContext(Context);

  const handleAddToCart = async (e, id) => {
    await addToCart(e, id);
    fetchUserAddToCart();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const categoryProduct = await fetchCategoryWiseProduct(category);
      setData(categoryProduct?.data || []);
    } catch (error) {
      console.error("Error fetching category products:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className='container mx-auto px-4 my-6 relative'>
      <h2 className='text-2xl font-semibold py-4'>{heading}</h2>

      <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,320px))] justify-between md:gap-6 overflow-x-scroll scrollbar-none transition-all'>
        {loading ? (
          loadingList.map((_, index) => (
            <div
              key={`loading-${index}`}
              className='w-full min-w-[280px] md:min-w-[320px] max-w-[280px] md:max-w-[320px] bg-white rounded-sm shadow'
            >
              <div className='bg-slate-200 h-48 p-4 min-w-[280px] md:min-w-[145px] flex justify-center items-center animate-pulse'>
                <span className='sr-only'>Loading product image</span>
              </div>
              <div className='p-4 grid gap-3'>
                <div className='h-6 bg-slate-200 animate-pulse rounded-full'>
                  <span className='sr-only'>Loading product name</span>
                </div>
                <div className='h-5 bg-slate-200 animate-pulse rounded-full'>
                  <span className='sr-only'>Loading category</span>
                </div>
                <div className='flex gap-3'>
                  <div className='h-5 bg-slate-200 animate-pulse rounded-full w-full'>
                    <span className='sr-only'>Loading price</span>
                  </div>
                  <div className='h-5 bg-slate-200 animate-pulse rounded-full w-full'>
                    <span className='sr-only'>Loading original price</span>
                  </div>
                </div>
                <div className='h-8 bg-slate-200 animate-pulse rounded-full'>
                  <span className='sr-only'>Loading add to cart button</span>
                </div>
              </div>
            </div>
          ))
        ) : data.length > 0 ? (
          data.map((product) => (
            <Link
              key={product._id}
              to={`/product/${product._id}`}
              className='w-full min-w-[280px] md:min-w-[320px] max-w-[280px] md:max-w-[320px] bg-white rounded-sm shadow hover:shadow-lg transition-shadow'
              onClick={scrollTop}
            >
              <div className='bg-slate-200 h-48 p-4 min-w-[280px] md:min-w-[145px] flex justify-center items-center'>
                <img
                  src={product.productImage[0]}
                  alt={product.productName}
                  className='object-scale-down h-full hover:scale-110 transition-all mix-blend-multiply'
                  loading="lazy"
                />
              </div>
              <div className='p-4 grid gap-3'>
                <h3 className='font-medium text-base md:text-lg text-ellipsis line-clamp-1 text-black'>
                  {product.productName}
                </h3>
                <p className='capitalize text-slate-500'>{product.category}</p>
                <div className='flex gap-3'>
                  <p className='text-green-600 font-medium'>
                    {displayINRCurrency(product.sellingPrice)}
                  </p>
                  <p className='text-slate-500 line-through'>
                    {displayINRCurrency(product.price)}
                  </p>
                </div>
                <button
                  className='text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-0.5 rounded-full transition-colors'
                  onClick={(e) => handleAddToCart(e, product._id)}
                  aria-label={`Add ${product.productName} to cart`}
                >
                  Add to Cart
                </button>
              </div>
            </Link>
          ))
        ) : (
          <div className='col-span-full text-center py-8'>
            <p className='text-slate-500 text-lg'>No products found in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryWiseProductDisplay;